import {
  TableColumn as TableColumnWSR,
  TableColumn as WSRTableColumn,
} from '@wix/design-system/dist/types/Table';
import { NestedTableKeyedItem, NestedTableState } from '../../state';
import { NestedTableParentCell } from './NestedTableParentCell';
import React from 'react';
import { useCreateWSRColumnBase } from '../../hooks/useCreateWSRColumnBase';
import { useNestedTableActionsCell } from './useNestedTableActionsCell';

export interface UseNestedModeColumnsProps<C extends string> {
  state: NestedTableState<C>;
  horizontalScroll?: boolean;
  actionsCellWidth?: string | number;
  actionsCellProps?: Partial<Omit<TableColumnWSR<any>, 'render' | 'style'>>;
}

export function useNestedModeColumns<C extends string>(
  props: UseNestedModeColumnsProps<C>,
): WSRTableColumn<NestedTableKeyedItem<any, any, any>>[] {
  const { state, actionsCellProps, actionsCellWidth, horizontalScroll } = props;

  const { toolbar } = state;

  const { selectedOrderedColumnsOrAll } = toolbar;

  const createWSRColumnBase = useCreateWSRColumnBase({ horizontalScroll });

  const actionsCellColumn = useNestedTableActionsCell({
    state: state.nested,
    actionsCellWidth,
    actionsCellProps,
  });

  return [
    {
      key: 'nested-mode-main-column',
      dataHook: 'nested-mode-main-column',
      title: '',
      width: '326px',
      ...state.mainColumn,
      style: undefined,
      render: (keyedItem, rowNum) => {
        if (keyedItem.item.originalKeyedItem) {
          return (
            <NestedTableParentCell
              state={keyedItem.item.state}
              item={keyedItem.item}
              rowNum={rowNum}
            />
          );
        }

        return null;
      },
    },
    ...selectedOrderedColumnsOrAll.map((column, index, { length }) => {
      const {
        id,
        title,
        sortable,
        infoTooltipProps,
        render,
        width,
        ...restColumn
      } = column;
      return {
        ...restColumn,
        ...createWSRColumnBase(column, index, length),
        sortDescending:
          sortable && id ? state.query.sort.getIsSortDescending(id) : undefined,
        render: (
          { item }: NestedTableKeyedItem<any, any, any>,
          rowNum: number,
        ) => {
          if (item.originalKeyedItem) {
            return item.state.columns?.[id]?.(
              item.originalKeyedItem.item,
              rowNum,
            );
          }

          return null;
        },
      };
    }),
    ...(actionsCellColumn ? [actionsCellColumn] : []),
  ];
}
